/**
 * Inloggen + registreren — nieuwe klanten krijgen een eigen organisatie (tenant) + proefperiode.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { TurnstileField } from '../components/TurnstileField';
import { api, fetchPublicConfig } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { SIGNUP_SOURCE_OPTIONS } from '../lib/signup-sources';

function PasswordField({
  id,
  autoComplete,
  value,
  onChange,
  minLength,
  required,
  reveal,
  onRevealChange,
}: {
  id: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  minLength?: number;
  required?: boolean;
  reveal: boolean;
  onRevealChange: (v: boolean) => void;
}) {
  return (
    <div className="relative">
      <input
        id={id}
        type={reveal ? 'text' : 'password'}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        className="w-full rounded-lg border border-border py-2 pl-3 pr-11 text-text"
        autoComplete={autoComplete}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Houd ingedrukt om het wachtwoord te tonen"
        title="Wachtwoord tonen (ingedrukt houden)"
        className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-text-muted hover:bg-surface hover:text-text"
        onPointerDown={(e) => {
          e.preventDefault();
          onRevealChange(true);
        }}
        onPointerUp={() => onRevealChange(false)}
        onPointerLeave={() => onRevealChange(false)}
        onPointerCancel={() => onRevealChange(false)}
      >
        <Eye className="h-4 w-4 shrink-0" aria-hidden />
      </button>
    </div>
  );
}

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [signupSource, setSignupSource] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [revealLoginPw, setRevealLoginPw] = useState(false);
  const [revealRegisterPw, setRevealRegisterPw] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  /** null = laden, string = site key, lege string = geen Turnstile */
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicConfig()
      .then((c) => {
        if (!cancelled) setTurnstileSiteKey(c.turnstile_site_key ?? '');
      })
      .catch(() => {
        if (!cancelled) setTurnstileSiteKey('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('register') === '1') {
      setMode('register');
      setError(null);
      setRegisterSuccess(false);
    }
  }, [searchParams]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'register' && !acceptTerms) {
      setError('Ga akkoord met de voorwaarden en het privacybeleid om een account aan te maken.');
      return;
    }
    if (mode === 'register' && !signupSource) {
      setError('Kies hoe je CalqTool hebt gevonden.');
      return;
    }
    if (mode === 'register' && password !== passwordConfirm) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }
    if (mode === 'register' && turnstileSiteKey === null) {
      setError('Een ogenblik, beveiliging laden…');
      return;
    }
    if (mode === 'register' && turnstileSiteKey !== '' && !turnstileToken) {
      setError('Rond de beveiligingscontrole hieronder af.');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        const loginData = await api.authLogin({ email, password });
        const revoked = loginData.older_sessions_revoked ?? 0;
        if (revoked > 0) {
          try {
            sessionStorage.setItem(
              'calq_session_notice',
              JSON.stringify({ kind: 'older_devices', count: revoked })
            );
          } catch {
            /* ignore */
          }
        }
        await refreshUser();
        const returnTo = searchParams.get('returnTo');
        const safe =
          returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/projects/new';
        navigate(safe, { replace: true });
      } else {
        await api.authRegister({
          email,
          password,
          password_confirm: passwordConfirm,
          ...(organizationName.trim() ? { organization_name: organizationName.trim() } : {}),
          signup_source: signupSource,
          ...(turnstileSiteKey ? { turnstile_token: turnstileToken ?? undefined } : {}),
        });
        await refreshUser();
        setRegisterSuccess(true);
        const returnTo = searchParams.get('returnTo');
        const safe =
          returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/projects/new';
        await new Promise((r) => setTimeout(r, 2200));
        navigate(safe, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/login"
        title={mode === 'login' ? 'Inloggen' : 'Registreren'}
        description={
          mode === 'login'
            ? 'Log in op CalqTool™ met je e-mail en wachtwoord. Toegang tot je calculatiedossiers, prijsbibliotheek en export — veilig sessiebeheer.'
            : 'Maak een CalqTool™-account aan en start direct met bouwkosten berekenen: prijsbibliotheek, dossiers en rapportage — Nederlands platform.'
        }
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: mode === 'login' ? 'Inloggen' : 'Registreren' }]} />
        <div className="mx-auto max-w-md rounded-lg border border-border bg-surface-card p-8">
          <h1 className="mb-1 text-2xl font-bold text-text">
            {mode === 'login' ? 'Inloggen' : 'Account aanmaken'}
          </h1>
          {searchParams.get('reden') === 'inactiviteit' && mode === 'login' && (
            <p
              className="mb-4 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50"
              role="status"
            >
              Je sessie is beëindigd na een periode van inactiviteit (beveiliging). Log opnieuw in om verder te gaan.
            </p>
          )}
          <p className="mb-6 text-sm text-text-muted">
            {mode === 'login'
              ? 'Log in met het e-mailadres en wachtwoord van je account.'
              : 'Start hier een nieuw bedrijfsaccount met proefperiode. Heb je een uitnodiging van je werkgever? Gebruik de link uit die e-mail — niet dit formulier.'}
          </p>

          <div className="mb-4 rounded-lg border border-border bg-surface px-3 py-2.5 text-xs leading-relaxed text-text-muted">
            <strong className="text-text">Collega die al CalqTool bij jullie gebruikt?</strong> Vraag de accountbeheerder
            om een uitnodiging. Registreren op deze pagina maakt altijd een <em>nieuw</em>, los bedrijfsaccount — handig
            voor een eigen proefperiode, maar niet om bij een bestaand team te horen.
          </div>

          <div className="mb-4 flex gap-2 rounded-lg bg-surface p-1">
            <button
              type="button"
              onClick={() => {
                setMode('login');
                setError(null);
                setAcceptTerms(false);
                setRegisterSuccess(false);
                setTurnstileToken(null);
              }}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                mode === 'login' ? 'bg-surface-card text-text shadow-sm' : 'text-text-muted'
              }`}
            >
              Inloggen
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('register');
                setError(null);
                setAcceptTerms(false);
                setRegisterSuccess(false);
                setTurnstileToken(null);
              }}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                mode === 'register' ? 'bg-surface-card text-text shadow-sm' : 'text-text-muted'
              }`}
            >
              Nieuw account
            </button>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            aria-describedby={error ? 'login-form-error' : undefined}
          >
            <div>
              <label htmlFor="login-email" className="mb-1 block text-sm font-medium text-text">
                E-mailadres
              </label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
                autoComplete="email"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label htmlFor="register-org" className="mb-1 block text-sm font-medium text-text">
                  Organisatie
                </label>
                <p className="mb-2 text-xs text-text-muted">
                  Naam van je bedrijf of team (voor je accountoverzicht). Laat leeg om automatisch een naam te laten
                  kiezen.
                </p>
                <input
                  id="register-org"
                  type="text"
                  autoComplete="organization"
                  value={organizationName}
                  onChange={(ev) => setOrganizationName(ev.target.value)}
                  maxLength={120}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  placeholder="Bijv. Bouwbedrijf Jansen"
                />
              </div>
            )}

            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label
                  htmlFor={mode === 'login' ? 'login-password' : 'register-password'}
                  className="block text-sm font-medium text-text"
                >
                  Wachtwoord
                </label>
                {mode === 'login' && (
                  <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
                    Wachtwoord vergeten?
                  </Link>
                )}
              </div>
              <PasswordField
                id={mode === 'login' ? 'login-password' : 'register-password'}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={setPassword}
                minLength={mode === 'register' ? 8 : 1}
                required
                reveal={mode === 'login' ? revealLoginPw : revealRegisterPw}
                onRevealChange={mode === 'login' ? setRevealLoginPw : setRevealRegisterPw}
              />
              {mode === 'register' && (
                <p className="mt-1 text-xs text-text-muted">Minimaal 8 tekens.</p>
              )}
            </div>

            {mode === 'register' && (
              <div>
                <label htmlFor="register-password-confirm" className="mb-1 block text-sm font-medium text-text">
                  Bevestig wachtwoord
                </label>
                <PasswordField
                  id="register-password-confirm"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={setPasswordConfirm}
                  minLength={8}
                  required
                  reveal={revealRegisterPw}
                  onRevealChange={setRevealRegisterPw}
                />
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label htmlFor="signup-source" className="mb-1 block text-sm font-medium text-text">
                  Hoe heb je CalqTool gevonden?
                </label>
                <p className="mb-2 text-xs text-text-muted">
                  Helpt ons te zien waar klanten ons vinden (intern gebruik).
                </p>
                <select
                  id="signup-source"
                  required
                  value={signupSource}
                  onChange={(ev) => setSignupSource(ev.target.value)}
                  className="w-full rounded-lg border border-border bg-surface-card px-3 py-2.5 text-sm text-text"
                >
                  <option value="">Maak een keuze…</option>
                  {SIGNUP_SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mode === 'register' && turnstileSiteKey !== null && turnstileSiteKey !== '' && (
              <div className="rounded-lg border border-border bg-surface px-3 py-2">
                <p className="mb-2 text-xs text-text-muted">Beveiligingscontrole</p>
                <TurnstileField siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
              </div>
            )}

            {mode === 'register' && (
              <div className="rounded-lg border border-border bg-surface px-3 py-3">
                <label className="flex cursor-pointer gap-3 text-sm text-text-muted">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(ev) => setAcceptTerms(ev.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>
                    Ik ga akkoord met de{' '}
                    <Link to="/terms" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
                      algemene voorwaarden
                    </Link>{' '}
                    en het{' '}
                    <Link to="/privacy" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
                      privacybeleid
                    </Link>
                    .                     Ik begrijp dat er een <strong className="text-text">proefperiode</strong> kan gelden (7 dagen en/of
                    gebruikslimieten — zie{' '}
                    <Link to="/prijzen" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
                      pakketten
                    </Link>
                    ). Daarna kan een betaald abonnement via Stripe (als accountbeheerder: Account → Facturatie).
                  </span>
                </label>
              </div>
            )}

            {registerSuccess && (
              <p
                className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-text"
                role="status"
                aria-live="polite"
              >
                <strong>Account aangemaakt.</strong> Controleer je inbox voor een welkomstmail (ook spam). Je wordt
                zo doorgestuurd…
              </p>
            )}
            {error && (
              <p
                id="login-form-error"
                className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100"
                role="alert"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || registerSuccess}
              className="w-full rounded-lg bg-primary py-3 font-medium text-white transition-all hover:bg-primary-alt disabled:opacity-60"
            >
              {loading ? 'Bezig…' : mode === 'login' ? 'Inloggen' : 'Account aanmaken'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-text-muted">
            <Link to="/" className="text-primary hover:underline">
              ← Terug naar home
            </Link>
          </p>
        </div>
      </AnimatedSection>
    </div>
  );
}
